'use client'

import { useEffect } from 'react'
import Link from 'next/link'
import { useTranslations } from 'next-intl'
import { CheckCircleIcon, XCircleIcon, LoaderIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useVerifyEmail } from '@/hooks/use-auth'
import { getErrorMessage } from '@/lib/utils'

type PropsType = {
  token: string
}

const EmailVerificationView = ({ token }: PropsType) => {
  const t = useTranslations()
  const { mutate, isPending, isSuccess, isError, error } = useVerifyEmail()

  useEffect(() => {
    mutate(token)
  }, [token, mutate])

  return (
    <div className="flex flex-col items-center gap-4 py-2 text-center">
      {isPending && (
        <>
          <LoaderIcon className="text-muted-foreground h-12 w-12 animate-spin" />
          <p className="text-muted-foreground text-sm">{t('email_verifying')}</p>
        </>
      )}

      {isSuccess && (
        <>
          <CheckCircleIcon className="h-12 w-12 text-green-500" />
          <div>
            <p className="font-semibold">{t('email_verified_success')}</p>
            <p className="text-muted-foreground mt-1 text-sm">{t('email_verified_success_description')}</p>
          </div>
          <Button asChild className="w-full">
            <Link href="/">{t('go_to_home')}</Link>
          </Button>
        </>
      )}

      {isError && (
        <>
          <XCircleIcon className="h-12 w-12 text-red-500" />
          <div>
            <p className="font-semibold">{t('email_verification_failed')}</p>
            <p className="text-muted-foreground mt-1 text-sm">
              {getErrorMessage(error, t('email_verification_failed_description'))}
            </p>
          </div>
          <Button asChild variant="outline" className="w-full">
            <Link href="/login">{t('back_to_login')}</Link>
          </Button>
        </>
      )}
    </div>
  )
}

export default EmailVerificationView
